import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, User, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

function StarRow({ value = 0, size = 16, onSelect, interactive = false }) {
    return (
        <div className="inline-flex gap-0.5" role="img" aria-label={`${value} stelle su 5`}>
            {[1, 2, 3, 4, 5].map((n) => {
                const filled = n <= Math.round(value);
                return (
                    <button
                        key={n}
                        type={interactive ? "button" : undefined}
                        onClick={interactive ? () => onSelect?.(n) : undefined}
                        disabled={!interactive}
                        className={interactive ? "cursor-pointer" : "cursor-default"}
                        data-testid={interactive ? `star-input-${n}` : undefined}
                        aria-label={`${n} stelle`}
                    >
                        <Star
                            size={size}
                            className={
                                filled
                                    ? "fill-bounty text-bounty"
                                    : "text-ink/25"
                            }
                        />
                    </button>
                );
            })}
        </div>
    );
}

export default function ReviewSection({ productHandle, productTitle }) {
    const [data, setData] = useState({
        reviews: [],
        total: 0,
        average: 0,
        distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    });
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        author: "",
        email: "",
        rating: 5,
        title: "",
        body: "",
    });
    const [submitting, setSubmitting] = useState(false);

    async function refresh() {
        setLoading(true);
        try {
            const { data } = await api.get(`/reviews/${productHandle}`);
            setData(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productHandle]);

    async function submit(e) {
        e.preventDefault();
        if (form.body.length < 5) {
            toast.error("Scrivi almeno qualche parola nella recensione");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/reviews/${productHandle}`, {
                ...form,
                email: form.email || null,
            });
            toast.success("Grazie pirata! Recensione pubblicata ⚓");
            setForm({ author: "", email: "", rating: 5, title: "", body: "" });
            setShowForm(false);
            refresh();
        } catch (err) {
            const msg = err?.response?.data?.detail || "Errore invio";
            toast.error(
                typeof msg === "string" ? msg : "Errore durante l'invio"
            );
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <section
            className="mt-24 pt-16 border-t-2 border-ink"
            data-testid="review-section"
        >
            <div className="grid lg:grid-cols-3 gap-10">
                {/* Left: summary */}
                <div className="lg:col-span-1">
                    <div className="font-hand text-2xl text-bounty dashed-underline inline-block">
                        pareri di ciurma
                    </div>
                    <h2 className="font-display text-4xl lg:text-5xl tracking-tight mt-3">
                        RECENSIONI
                    </h2>

                    <div className="mt-6 bounty-card p-5">
                        <div className="flex items-end gap-3">
                            <div
                                className="font-display text-6xl leading-none text-bounty"
                                data-testid="reviews-average"
                            >
                                {data.total > 0 ? data.average.toFixed(1) : "—"}
                            </div>
                            <div className="pb-1">
                                <StarRow value={data.average} size={18} />
                                <div className="font-mono text-xs text-ink/60 mt-1">
                                    {data.total}{" "}
                                    {data.total === 1
                                        ? "recensione"
                                        : "recensioni"}
                                </div>
                            </div>
                        </div>

                        {/* Distribution bars */}
                        <div className="mt-5 space-y-1.5">
                            {[5, 4, 3, 2, 1].map((n) => {
                                const c = data.distribution?.[n] || 0;
                                const pct =
                                    data.total > 0 ? (c / data.total) * 100 : 0;
                                return (
                                    <div
                                        key={n}
                                        className="flex items-center gap-2 text-xs"
                                    >
                                        <span className="font-mono w-4">
                                            {n}★
                                        </span>
                                        <div className="flex-1 h-3 border-2 border-ink bg-white overflow-hidden">
                                            <div
                                                className="h-full bg-bounty"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                        <span className="font-mono text-ink/60 w-6 text-right">
                                            {c}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        <button
                            type="button"
                            onClick={() => setShowForm(!showForm)}
                            className="btn-manga btn-manga-red w-full mt-6"
                            data-testid="write-review-btn"
                        >
                            {showForm
                                ? "Chiudi modulo"
                                : "Scrivi una recensione"}
                        </button>
                    </div>
                </div>

                {/* Right: form + list */}
                <div className="lg:col-span-2 space-y-8">
                    {showForm && (
                        <motion.form
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            onSubmit={submit}
                            className="bounty-card p-6 space-y-4"
                            data-testid="review-form"
                        >
                            <div className="font-hand text-xl text-bounty">
                                lascia il tuo parere
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    required
                                    placeholder="Il tuo nome da pirata *"
                                    value={form.author}
                                    onChange={(e) =>
                                        setForm({ ...form, author: e.target.value })
                                    }
                                    className="input-manga"
                                    data-testid="review-author-input"
                                />
                                <input
                                    type="email"
                                    placeholder="Email (opzionale)"
                                    value={form.email}
                                    onChange={(e) =>
                                        setForm({ ...form, email: e.target.value })
                                    }
                                    className="input-manga"
                                    data-testid="review-email-input"
                                />
                            </div>
                            <div>
                                <div className="font-mono text-xs tracking-widest uppercase mb-1">
                                    Il tuo voto
                                </div>
                                <StarRow
                                    value={form.rating}
                                    size={28}
                                    interactive
                                    onSelect={(n) =>
                                        setForm({ ...form, rating: n })
                                    }
                                />
                            </div>
                            <input
                                type="text"
                                required
                                placeholder="Titolo della recensione *"
                                value={form.title}
                                onChange={(e) =>
                                    setForm({ ...form, title: e.target.value })
                                }
                                className="input-manga"
                                data-testid="review-title-input"
                            />
                            <textarea
                                required
                                rows={4}
                                placeholder={`Cosa ne pensi di "${productTitle}"? *`}
                                value={form.body}
                                onChange={(e) =>
                                    setForm({ ...form, body: e.target.value })
                                }
                                className="input-manga resize-none"
                                data-testid="review-body-input"
                            />
                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowForm(false)}
                                    className="btn-manga btn-manga-ghost"
                                >
                                    Annulla
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-manga btn-manga-red"
                                    data-testid="review-submit-btn"
                                >
                                    {submitting ? "..." : "Pubblica"}
                                </button>
                            </div>
                        </motion.form>
                    )}

                    {loading ? (
                        <div className="font-hand text-2xl text-ink/40 text-center py-8">
                            caricamento...
                        </div>
                    ) : data.reviews.length === 0 ? (
                        <div className="bounty-card p-8 text-center">
                            <Sparkles
                                size={40}
                                className="mx-auto text-bounty mb-3"
                            />
                            <div className="font-hand text-2xl text-bounty">
                                Sii il primo a raccontare la tua avventura
                            </div>
                            <p className="text-sm text-ink/70 mt-2">
                                Nessuna recensione ancora per questa figura.
                            </p>
                        </div>
                    ) : (
                        <div
                            className="space-y-4"
                            data-testid="reviews-list"
                        >
                            {data.reviews.map((r) => (
                                <motion.div
                                    key={r.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bounty-card p-5"
                                    data-testid={`review-item-${r.id}`}
                                >
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div>
                                            <StarRow value={r.rating} size={14} />
                                            <div className="font-display text-xl tracking-tight mt-1">
                                                {r.title}
                                            </div>
                                        </div>
                                        <div className="font-mono text-xs text-ink/50 flex items-center gap-1">
                                            <User size={12} /> {r.author}
                                            <span className="mx-1">·</span>
                                            {new Date(
                                                r.created_at
                                            ).toLocaleDateString("it-IT")}
                                        </div>
                                    </div>
                                    <p className="mt-3 text-ink/80 leading-relaxed whitespace-pre-wrap text-sm">
                                        {r.body}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
